import { Button } from '@teamsparta/stack-button';
import { Flex } from '@teamsparta/stack-flex';
import { ActionDownload, ActionShare } from '@teamsparta/stack-icons';
import { toast } from '@teamsparta/stack-toast';
import { vars } from '@teamsparta/stack-tokens';
import { domToPng } from 'modern-screenshot';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useRef } from 'react';

import type { GrassCampaignResponseDto } from '@/api/campaign/grass/grass.dto';
import Only from '@/components/common/Only';
import { downloadFileFromUrl } from '@/lib/utils/download';
import { useGrassDonationModal } from '@/store/campaign';

import { copyCurrentUrl, getGithubDisplayMessage } from '../../logic';
import {
  getDonationCertificateData,
  getUserIdFromStorage,
  useModalHistory,
  useModalScroll,
} from './logic';
import * as S from './style';

interface GrassDonationModalProps {
  grassData: GrassCampaignResponseDto;
}

export function GrassDonationModal({ grassData }: GrassDonationModalProps) {
  const router = useRouter();

  const { value: isModalOpen, close } = useGrassDonationModal();
  const donationCertificateRef = useRef<HTMLDivElement>(null);

  useModalScroll(isModalOpen, router);
  const { handleClose } = useModalHistory(isModalOpen, close, router);

  const certificateData = getDonationCertificateData(
    grassData?.totalContributionsIn2024,
  );

  const handleClickCaptureButton = async () => {
    const donationCertificate = donationCertificateRef.current;
    if (!donationCertificate) {
      return;
    }

    let image;
    try {
      image = await domToPng(donationCertificate, {
        quality: 1,
        scale: 6,
      });
    } catch (error) {
      toast.error('이미지 저장에 실패했습니다.', {
        position: 'bottom-center',
      });
      return;
    }

    downloadFileFromUrl({
      url: image,
      name: '잔디 기부 캠페인 기부증',
      type: 'image/png',
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClickMobileShare = async () => {
    try {
      await navigator.share({
        title: '잔디 기부 캠페인',
        text: `내 잔디 ${grassData?.totalContributionsIn2024}개가 기부되어 트리가 채워졌어요`,
        url: window.location.href,
      });
    } catch (error) {
      toast.warning('공유하기가 취소되었습니다.', {
        position: 'bottom-center',
      });
    }
  };

  if (!grassData || !isModalOpen) {
    return null;
  }

  const githubUserId = getUserIdFromStorage();

  return (
    <S.Overlay onClick={handleOverlayClick}>
      <S.ModalContainer>
        <S.ContentWrapper>
          <S.CloseButton onClick={handleClose}>
            <Image
              src='/images/campaign/campaign_x_icon.svg'
              width={24}
              height={24}
              alt='modal_close'
            />
          </S.CloseButton>

          <S.Title>
            {getGithubDisplayMessage(grassData, router.query.id)}
          </S.Title>

          <S.Description>
            특별한 아이템이 담긴 나만의 기부증을 공유해 보세요.
          </S.Description>
          <S.ModalDonationCertificateWrapper ref={donationCertificateRef}>
            <Flex.Column align='center' gap='10px'>
              <S.CertificateName>잔디 기부 캠페인 기부증</S.CertificateName>
              <S.CertificateImageWrapper>
                <Image
                  src={certificateData.imagePath}
                  fill
                  alt='certificate_image'
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </S.CertificateImageWrapper>
            </Flex.Column>
            <Flex.Column
              justify='center'
              align='center'
              gap='3px'
              style={{ width: '233px' }}
            >
              <S.CertificateTitle>{certificateData.title}</S.CertificateTitle>
              <S.CertificateDescription>
                {certificateData.description}
              </S.CertificateDescription>
            </Flex.Column>
            <Flex.Column align='center' gap='4px'>
              <S.CertificateBody>
                <div>장식 레벨</div>
                <div>Lv. {certificateData.level}</div>
              </S.CertificateBody>
              <S.CertificateBody>
                <div>기부 잔디 수</div>
                <div>{grassData?.totalContributionsIn2024} 개</div>
              </S.CertificateBody>
            </Flex.Column>
            <div>
              <Image
                src='/images/campaign/campaign_certificate_logo.png'
                alt='hanghae_logo'
                width={44}
                height={14}
                style={{
                  objectFit: 'contain',
                }}
              />
            </div>
          </S.ModalDonationCertificateWrapper>
        </S.ContentWrapper>
        {githubUserId === router.query.id && (
          <S.ButtonWrapper>
            <Button
              onClick={handleClickCaptureButton}
              size='md'
              colorScheme='secondary'
              variant='outline'
              radius='rounded'
              fullWidth
              rightAddon={<ActionDownload size={16} color={vars.neutral[80]} />}
            >
              이미지 저장
            </Button>
            <Only.Desktop>
              <Button
                onClick={copyCurrentUrl}
                size='md'
                colorScheme='primary'
                variant='solid'
                radius='rounded'
                fullWidth
                rightAddon={<ActionShare size={16} color='#fff' />}
              >
                공유하기
              </Button>
            </Only.Desktop>
            <Only.Mobile>
              <Button
                onClick={handleClickMobileShare}
                size='md'
                colorScheme='primary'
                variant='solid'
                radius='rounded'
                fullWidth
                rightAddon={<ActionShare size={16} color='#fff' />}
              >
                공유하기
              </Button>
            </Only.Mobile>
          </S.ButtonWrapper>
        )}
      </S.ModalContainer>
    </S.Overlay>
  );
}
